import React from 'react';
import classNames from 'styles/utils/classNames';

export interface TabOption<T extends string> {
  label: string;
  /** Shorter label shown on mobile only (keeps tabs from overflowing). Falls back to `label`. */
  shortLabel?: string;
  value: T;
  /** Image icon (rendered as <img>). */
  iconSrc?: string;
  /** Swapped in for `iconSrc` while the tab is selected (Figma's "Orange" icon variant). */
  activeIconSrc?: string;
  /** SVG icon component (rendered inline, takes `className`). Takes precedence over `iconSrc`. */
  Icon?: React.ComponentType<{ className?: string }>;
  /** Extra classes for the icon — e.g. a `text-*` color for a `currentColor` SVG. */
  iconClassName?: string;
  /** Small brand count pinned to the tab's top-right corner; hidden at 0. */
  badgeCount?: number;
}

export interface TabSwitcherProps<T extends string> {
  options: TabOption<T>[];
  selectedValue: T;
  onValueChange: (value: T) => void;
  wrapperClassName?: string;
  /** Extra classes for every tab button, e.g. a responsive label size. */
  buttonClassName?: string;
  /** When true, selected tab uses brand fill (bg + text) instead of primary fill */
  activeFillBrand?: boolean;
  /** When true, selected tab is a solid dark pill with inverted text (Figma "Switch"). */
  activeFillInvert?: boolean;
  /** When true, unselected tab icons keep their full color instead of being greyed out */
  keepIconColor?: boolean;
}

const TabSwitcher = <T extends string>({
  options,
  selectedValue,
  onValueChange,
  wrapperClassName,
  buttonClassName,
  activeFillBrand = false,
  activeFillInvert = false,
  keepIconColor = false,
}: TabSwitcherProps<T>) => {
  return (
    <div
      className={classNames(
        'inline-flex w-full gap-0 rounded-full bg-color-bg-lightmode-tertiary p-1 dark:bg-color-bg-darkmode-tertiary',
        wrapperClassName,
      )}
    >
      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        const iconClasses = classNames(
          'shrink-0 transition-all duration-200',
          !isSelected && !keepIconColor && 'opacity-60 grayscale',
          // `iconClassName` (when set) owns the icon size + color; otherwise fall back to the
          // default. (Plain `classNames` join doesn't merge Tailwind, so a default `h-6 w-6`
          // here would beat an override by stylesheet order.)
          option.iconClassName ?? 'h-6 w-6 max-sm:h-5 max-sm:w-5',
        );
        const selectedClasses = activeFillBrand
          ? 'bg-color-brand-highlight text-color-text-darkmode-primary shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.06)]'
          : activeFillInvert
          ? 'bg-color-bg-lightmode-invert text-color-text-lightmode-invert shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.06)] dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert'
          : 'bg-color-bg-lightmode-primary text-color-text-lightmode-primary shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.06)] dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-primary';
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onValueChange(option.value)}
            className={classNames(
              'typography-product-button-label-medium max-sm:typography-product-button-label-small relative flex h-full flex-1 items-center justify-center gap-2 rounded-full px-3 py-2 transition-all duration-200 max-sm:gap-1',
              isSelected
                ? selectedClasses
                : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
              buttonClassName,
            )}
          >
            {option.Icon ? (
              <option.Icon className={iconClasses} />
            ) : (
              option.iconSrc && (
                <img
                  src={(isSelected && option.activeIconSrc) || option.iconSrc}
                  alt={option.label}
                  className={classNames(iconClasses, 'object-contain')}
                />
              )
            )}
            <span className="whitespace-nowrap">
              {option.shortLabel ? (
                <>
                  <span className="max-sm:hidden">{option.label}</span>
                  <span className="sm:hidden">{option.shortLabel}</span>
                </>
              ) : (
                option.label
              )}
            </span>
            {!!option.badgeCount && (
              <span className="absolute right-0 top-0 flex h-3 min-w-3 items-center justify-center rounded-full bg-color-bg-lightmode-brand px-0.5 text-[8px] font-semibold leading-3 tracking-[-0.02em] text-color-text-darkmode-primary">
                {option.badgeCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default TabSwitcher;
